import { useState, useRef, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import * as IBAN from "iban";
import { toast, Bounce, type ToastOptions } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import { getSessionFromCookie } from "../utils/cognito";
import { getFirstOfMonthOnOrAfter } from "../../utils/dateUtils";
import { signBessResignContract, BessResignError } from "../../api/bessResign";
import { getProductLabelFromId } from "../../functions/flexServices";
import type {
  BessResignPendingRow,
  BessResignRegenerateBillingInfo,
  BessResignSignBillingInfo,
  BessResignSignRequest,
} from "../../types/bessResignTypes";
import { PendingToast } from "../ui/toastResponds/PendingToast";
import { SuccessToast } from "../ui/toastResponds/SuccessToast";
import { ErrorToast } from "../ui/toastResponds/ErrorToast";
import { WarningToast } from "../ui/toastResponds/WarningToast";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface BessResignContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignSuccess: (newContractId: number, startDate: string) => void;
  pendingRow: BessResignPendingRow;
  pdfUrl: string;
  initialBillingInfo: BessResignRegenerateBillingInfo;
  projectedStartDate: string;
  signerName?: string;
}

const TOAST_OPTIONS = {
  position: "bottom-right",
  autoClose: 7000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "light",
  icon: false,
  transition: Bounce,
  style: { width: "400px", maxWidth: "800px" },
} satisfies ToastOptions;

const formatIbanWithSpaces = (value: string) =>
  value.replace(/\s/g, "").toUpperCase().match(/.{1,4}/g)?.join(" ") ?? value.toUpperCase();

const mapInitialBilling = (info: BessResignRegenerateBillingInfo | undefined): BessResignSignBillingInfo => {
  const street = [info?.address, info?.addressNumber].filter(Boolean).join(" ").trim();
  return {
    email: info?.email ?? "",
    iban: info?.iban ? formatIbanWithSpaces(info.iban) : "",
    address: street,
    city: info?.city ?? "",
    zip: info?.zip ?? "",
    country: info?.country || "Belgium",
  };
};

export const BessResignContractModal = ({
  isOpen,
  onClose,
  onSignSuccess,
  pendingRow,
  pdfUrl,
  initialBillingInfo,
  projectedStartDate,
  signerName = "",
}: BessResignContractModalProps) => {
  const { t } = useTranslation("bessresign");
  const productLabel = getProductLabelFromId(pendingRow.product_id);

  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);

  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [zoom, setZoom] = useState(1);

  const [signatureName, setSignatureName] = useState("");
  const [signerFunction, setSignerFunction] = useState("");

  const [signatureMode, setSignatureMode] = useState<"type" | "draw" | "upload">("type");
  const [canvasHasDrawing, setCanvasHasDrawing] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState<string | null>(null);

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [billingInfo, setBillingInfo] = useState<BessResignSignBillingInfo>(() =>
    mapInitialBilling(initialBillingInfo),
  );
  const [showBillingExpanded, setShowBillingExpanded] = useState(true);
  const [showBillingEdit, setShowBillingEdit] = useState(false);
  const [ibanValid, setIbanValid] = useState<boolean | null>(() => {
    const cleanIban = mapInitialBilling(initialBillingInfo).iban.replace(/\s/g, "").toUpperCase();
    return cleanIban ? IBAN.isValid(cleanIban) : null;
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const viewedPagesRef = useRef<Set<number>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  const validateIBAN = (value: string) => {
    const cleanIBAN = value.replace(/\s/g, "").toUpperCase();
    if (!cleanIBAN) {
      setIbanValid(null);
      return;
    }
    setIbanValid(IBAN.isValid(cleanIBAN));
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleBillingInputChange = (field: keyof BessResignSignBillingInfo, value: string) => {
    if (field === "iban") {
      const formatted = formatIbanWithSpaces(value);
      setBillingInfo((prev) => ({ ...prev, iban: formatted }));
      validateIBAN(formatted);
      return;
    }
    setBillingInfo((prev) => ({ ...prev, [field]: value }));
  };

  const mapSignError = useCallback(
    (err: unknown): string => {
      if (err instanceof BessResignError) {
        switch (err.status) {
          case 400:
            return t("signError400");
          case 403:
            return t("signError403");
          case 404:
            return t("signError404");
          case 409:
            return t("signError409");
          case 410:
            return t("signError410");
          case 422:
            // Backend guard: site has no certified controller (422 CONTROLLER_REQUIRED).
            return t("controllerRequired.toast", { ns: "bessOnboarding" });
          case 401:
            return t("regenerateErrorAuth");
          default:
            return t("signError500");
        }
      }
      return t("signError500");
    },
    [t],
  );

  useEffect(() => {
    if (!isOpen) return;
    const seededBilling = mapInitialBilling(initialBillingInfo);
    const cleanIban = seededBilling.iban.replace(/\s/g, "").toUpperCase();
    setPdfLoadError(null);
    setTotalPages(0);
    setCurrentPage(1);
    setScrollProgress(0);
    setZoom(1);
    setSignatureName("");
    setSignerFunction("");
    setSignatureMode("type");
    setCanvasHasDrawing(false);
    setUploadedSignature(null);
    setHasAcceptedTerms(false);
    setBillingInfo(seededBilling);
    setShowBillingExpanded(true);
    setShowBillingEdit(false);
    setIbanValid(cleanIban ? IBAN.isValid(cleanIban) : null);
    viewedPagesRef.current.clear();
  }, [isOpen, initialBillingInfo]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const pct = Math.min(100, Math.round((scrollTop / (scrollHeight - clientHeight)) * 100));
    setScrollProgress((prev) => Math.max(prev, isNaN(pct) ? 0 : pct));
    if (totalPages > 0) {
      const pageHeight = scrollHeight / totalPages;
      const visiblePage = Math.ceil((scrollTop + clientHeight / 2) / pageHeight);
      setCurrentPage(Math.min(visiblePage, totalPages));
      viewedPagesRef.current.add(visiblePage);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
    viewedPagesRef.current.add(1);
    setTimeout(() => {
      const container = scrollContainerRef.current;
      if (container && container.scrollHeight <= container.clientHeight) {
        setScrollProgress(100);
      }
    }, 100);
    setPdfLoadError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("Error loading PDF:", error);
    setPdfLoadError(t("regenerateError500"));
  };

  const handleAcceptTermsChange = (checked: boolean) => {
    setHasAcceptedTerms(checked);
    if (checked && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      setTimeout(() => {
        setScrollProgress(100);
        for (let i = 1; i <= totalPages; i++) viewedPagesRef.current.add(i);
      }, 500);
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(
      (e.clientX - rect.left) * (canvas.width / rect.width),
      (e.clientY - rect.top) * (canvas.height / rect.height),
    );
  };

  useEffect(() => {
    if (signatureMode !== "draw") return;

    const handleMove = (e: MouseEvent) => {
      if (!isDrawingRef.current || !canvasRef.current) return;
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.lineTo(
        (e.clientX - rect.left) * (canvas.width / rect.width),
        (e.clientY - rect.top) * (canvas.height / rect.height),
      );
      ctx.strokeStyle = "#04344d";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
      setCanvasHasDrawing(true);
    };

    const handleUp = () => {
      isDrawingRef.current = false;
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [signatureMode]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    setCanvasHasDrawing(false);
  };

  const buildSignatureImage = (): string | null => {
    if (signatureMode === "draw") {
      return canvasRef.current?.toDataURL("image/png") ?? null;
    }
    if (signatureMode === "upload") {
      return uploadedSignature;
    }
    const offscreen = document.createElement("canvas");
    offscreen.width = 300;
    offscreen.height = 80;
    const ctx = offscreen.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, 300, 80);
    ctx.font = "italic 28px Georgia, serif";
    ctx.fillStyle = "#04344d";
    ctx.textBaseline = "middle";
    ctx.fillText(signatureName.trim() || signerName, 16, 40);
    return offscreen.toDataURL("image/png");
  };

  const handleSign = async () => {
    const session = getSessionFromCookie();
    const token = session?.getAccessToken().getJwtToken();
    if (!token) {
      toast(<ErrorToast title={t("errorTitle")} message={t("errorAuthRequired")} />, TOAST_OPTIONS);
      return;
    }

    const signatureImage = buildSignatureImage();
    if (!signatureImage) return;

    const payload: BessResignSignRequest = {
      contract_id: pendingRow.contract_id,
      signature_name: signatureName.trim() || signerName,
      signed_at: new Date().toISOString().split("T")[0],
      billing_info: {
        ...billingInfo,
        iban: billingInfo.iban.replace(/\s/g, ""),
      },
      signature_image: signatureImage,
      customer_signatory_title: "",
      signee_function: signerFunction.trim(),
    };

    setIsProcessing(true);
    const signPromise = signBessResignContract(payload, token);
    toast.promise(
      signPromise,
      {
        pending: {
          render: () => <PendingToast title={t("signPendingTitle")} message={t("signPendingMessage")} />,
        },
        success: {
          render: ({ data }: { data: { startDate: string } }) => (
            <SuccessToast
              title={t("signSuccessTitle")}
              message={t("signSuccessMessage", { product: productLabel, startDate: data.startDate })}
            />
          ),
        },
        error: {
          render: ({ data }: { data: unknown }) => (
            <ErrorToast title={t("errorTitle")} message={mapSignError(data)} />
          ),
        },
      },
      TOAST_OPTIONS,
    );

    try {
      const response = await signPromise;

      if (!response.signedContract.uploaded) {
        toast(
          <WarningToast title={t("uploadWarningTitle")} message={t("uploadWarningMessage")} />,
          TOAST_OPTIONS,
        );
      }

      onSignSuccess(response.new_contract_id, response.startDate);
    } catch {
      // Error toast handled by toast.promise above
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    try {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `bess-resign-contract-${pendingRow.contract_id}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      window.open(pdfUrl, "_blank");
    }
  };

  if (!isOpen) return null;

  // Informational only — the backend rolls a past start date forward at signing time; signing stays enabled.
  const todayStr = new Date().toISOString().split("T")[0];
  const projectedStartDay = (projectedStartDate || "").slice(0, 10);
  const startDatePassed = !!projectedStartDay && projectedStartDay < todayStr;

  const documentReviewed = scrollProgress >= 80;
  const nameFilled = signerName ? true : signatureName.trim().length > 0;
  const functionFilled = signerFunction.trim().length > 0;
  const signatureValid =
    signatureMode === "type"
      ? signerName
        ? signatureName === signerName
        : signatureName.trim().length > 0
      : signatureMode === "draw"
        ? canvasHasDrawing
        : uploadedSignature !== null;
  const billingValid =
    ibanValid === true &&
    validateEmail(billingInfo.email) &&
    !!billingInfo.address &&
    !!billingInfo.city &&
    !!billingInfo.zip &&
    !!billingInfo.country;
  const canSign =
    documentReviewed &&
    nameFilled &&
    functionFilled &&
    hasAcceptedTerms &&
    signatureValid &&
    billingValid &&
    !isProcessing;

  const lockHint = !documentReviewed
    ? t("lockHintScroll")
    : !nameFilled || !functionFilled
      ? t("lockHintFunction")
      : !signatureValid
        ? t("lockHintSignature")
        : !hasAcceptedTerms
          ? t("lockHintTerms")
          : !billingValid
            ? t("lockHintBilling")
            : "";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-[1100px] flex flex-col"
        style={{ maxHeight: "95vh" }}
      >
        <div className="bg-primary-950 px-5 py-3 flex items-center justify-between flex-shrink-0 rounded-t-xl">
          <div>
            <h2 className="text-sm font-semibold text-white">{t("modalTitle", { product: productLabel })}</h2>
            <p className="text-xs text-primary-300 mt-0.5">
              {totalPages > 0
                ? `Page ${currentPage} of ${totalPages}`
                : t("modalSubtitle")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/10 border border-white/15 text-white hover:bg-white/20 transition-colors"
            aria-label={t("closeButton")}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex-1 flex flex-col bg-gray-200 min-w-0">
            <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 4) / 4))}
                  className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-medium leading-none"
                  title="Zoom out"
                >
                  −
                </button>
                <span className="text-xs text-gray-600 w-10 text-center tabular-nums">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.25) * 4) / 4))}
                  className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm font-medium leading-none"
                  title="Zoom in"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleDownload}
                disabled={!pdfUrl}
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 border border-gray-300 px-3 py-1 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-3.5 h-3.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                  />
                </svg>
                {t("downloadPdf")}
              </button>
            </div>

            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-auto p-4 flex flex-col items-start gap-0"
            >
              {pdfLoadError ? (
                <div className="flex items-center justify-center w-full h-full">
                  <div className="rounded-lg bg-red-50 border border-red-100 p-5 text-center max-w-sm">
                    <p className="font-semibold text-red-800">{pdfLoadError}</p>
                  </div>
                </div>
              ) : (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-400" />
                    </div>
                  }
                  className="flex flex-col items-center mx-auto"
                >
                  {totalPages > 0 &&
                    Array.from(new Array(totalPages), (_, index) => (
                      <div key={`page_${index + 1}`} className="mb-2">
                        <Page
                          pageNumber={index + 1}
                          width={Math.round(720 * zoom)}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          className="shadow-lg"
                        />
                      </div>
                    ))}
                </Document>
              )}
            </div>

            <div className="bg-gray-300/80 border-t border-gray-300 text-center py-1.5 text-xs text-gray-600 flex-shrink-0 flex items-center justify-center gap-1.5">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-3 h-3"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3" />
              </svg>
              {t("scrollHint")}
            </div>
          </div>

          <div className="w-[290px] flex-shrink-0 flex flex-col border-l border-gray-200 bg-gray-50 overflow-y-auto">
            <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-gray-700">Document review</span>
                <span
                  className={`text-xs font-semibold ${documentReviewed ? "text-green-600" : "text-primary-700"}`}
                >
                  {scrollProgress}%
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    documentReviewed ? "bg-green-600" : "bg-primary-600"
                  }`}
                  style={{ width: `${scrollProgress}%` }}
                />
              </div>
            </div>

            <div className="flex-shrink-0 px-3 py-2 bg-white border-b border-gray-200">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                {t("contractSummaryHeading")}
              </p>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">{t("siteLabel")}</span>
                  <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">
                    {pendingRow.site_name || "—"}
                  </span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">{t("installationLabel")}</span>
                  <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">
                    {pendingRow.installation_id}
                  </span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                  <span className="text-gray-400 flex-shrink-0">{t("newStartDateLabel")}</span>
                  <span className="text-gray-700 font-medium ml-2">{projectedStartDate}</span>
                </div>
                <div className="flex justify-between px-2.5 py-1 text-[11px]">
                  <span className="text-gray-400 flex-shrink-0">{t("originalSignatureDateLabel")}</span>
                  <span className="text-gray-700 font-medium ml-2">{pendingRow.current_signature_date}</span>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                {t("signeeHeading")}
              </p>
              <div className="mb-2">
                <label className="font-medium text-gray-700 text-xs block mb-1">
                  {t("fullNameLabel")} <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={signerName || signatureName}
                  readOnly={!!signerName}
                  onChange={signerName ? undefined : (e) => setSignatureName(e.target.value)}
                  placeholder={signerName ? undefined : t("fullNamePlaceholder")}
                  className={`rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs shadow-sm ${
                    signerName
                      ? "bg-gray-50 text-gray-500 cursor-default"
                      : "bg-white text-gray-700 focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10"
                  }`}
                  disabled={isProcessing}
                />
              </div>
              <div className="mb-2">
                <label className="font-medium text-gray-700 text-xs block mb-1">
                  {t("functionLabel")} <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={signerFunction}
                  onChange={(e) => setSignerFunction(e.target.value)}
                  placeholder={t("functionPlaceholder")}
                  className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10 bg-white text-gray-700 shadow-sm"
                  disabled={isProcessing}
                />
              </div>
            </div>

            <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-200">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                {t("signatureHeading")}
              </p>
              <div className="flex border border-gray-200 rounded-lg overflow-hidden mb-2">
                <button
                  onClick={() => setSignatureMode("type")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                    signatureMode === "type" ? "bg-primary-700 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {t("signatureModeType")}
                </button>
                <button
                  onClick={() => setSignatureMode("draw")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                    signatureMode === "draw" ? "bg-primary-700 text-white" : "bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {t("signatureModeDraw")}
                </button>
                <button
                  onClick={() => setSignatureMode("upload")}
                  className={`flex-1 py-1.5 text-xs font-medium transition-colors ${
                    signatureMode === "upload"
                      ? "bg-primary-700 text-white"
                      : "bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {t("signatureModeUpload")}
                </button>
              </div>

              {signatureMode === "type" ? (
                <div>
                  {signerName && (
                    <input
                      type="text"
                      value={signatureName}
                      onChange={(e) => setSignatureName(e.target.value)}
                      placeholder={t("signatureTypePlaceholder", { name: signerName })}
                      className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/10 bg-white text-gray-700 shadow-sm mb-1.5"
                      disabled={isProcessing}
                    />
                  )}
                  <div className="h-12 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                    <span
                      className="text-primary-900 opacity-80"
                      style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "18px" }}
                    >
                      {signatureName || signerName || "—"}
                    </span>
                  </div>
                  {signerName && signatureName && signatureName !== signerName && (
                    <p className="text-[10px] text-red-600 mt-1">
                      {t("signatureMismatch", { name: signerName })}
                    </p>
                  )}
                </div>
              ) : signatureMode === "draw" ? (
                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    width={258}
                    height={120}
                    onMouseDown={startDrawing}
                    className="border border-gray-200 rounded-lg bg-white cursor-crosshair w-full"
                    style={{ touchAction: "none" }}
                  />
                  {!canvasHasDrawing && (
                    <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-300 pointer-events-none">
                      {t("drawHere")}
                    </span>
                  )}
                  {canvasHasDrawing && (
                    <button
                      onClick={clearCanvas}
                      className="absolute bottom-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600"
                    >
                      {t("clearSignature")}
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  {uploadedSignature ? (
                    <div className="relative border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                      <img
                        src={uploadedSignature}
                        alt="Uploaded signature"
                        className="w-full h-[120px] object-contain p-2"
                      />
                      <button
                        onClick={() => setUploadedSignature(null)}
                        className="absolute top-1.5 right-2 text-[10px] text-gray-400 hover:text-gray-600 bg-white rounded px-1.5 py-0.5 border border-gray-200"
                      >
                        {t("uploadRemove")}
                      </button>
                    </div>
                  ) : (
                    <label
                      className="flex flex-col items-center justify-center h-[120px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 hover:border-gray-400 transition-colors"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
                          const reader = new FileReader();
                          reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                    >
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setUploadedSignature(ev.target?.result as string);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6 text-gray-400 mb-1.5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                        />
                      </svg>
                      <span className="text-xs text-gray-500 text-center px-2 whitespace-pre-line">
                        {t("uploadDropHint")}
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>

            <div className="flex-shrink-0 px-3 py-2 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowBillingExpanded(!showBillingExpanded)}
                  className="flex items-center gap-1.5 flex-1 min-w-0"
                >
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                    {t("billingHeading")}
                  </p>
                  <svg
                    className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${
                      showBillingExpanded ? "rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  {!showBillingExpanded && pendingRow.company_name && (
                    <span className="text-[11px] text-gray-500 truncate ml-1">{pendingRow.company_name}</span>
                  )}
                </button>
                {showBillingExpanded && (
                  <button
                    onClick={() => setShowBillingEdit(!showBillingEdit)}
                    className="text-[11px] font-medium text-primary-700 hover:text-primary-800 flex-shrink-0 ml-2"
                  >
                    {showBillingEdit ? t("billingDoneButton") : t("billingEditButton")}
                  </button>
                )}
              </div>

              {showBillingExpanded && !showBillingEdit && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 mt-1.5">
                  {pendingRow.company_name && (
                    <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                      <span className="text-gray-400 flex-shrink-0">{t("companyLabel")}</span>
                      <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">
                        {pendingRow.company_name}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                    <span className="text-gray-400 flex-shrink-0">{t("billingEmail")}</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">
                      {billingInfo.email || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                    <span className="text-gray-400 flex-shrink-0">{t("billingIban")}</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">
                      {billingInfo.iban || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between px-2.5 py-1 text-[11px] border-b border-gray-200">
                    <span className="text-gray-400 flex-shrink-0">{t("billingAddress")}</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">
                      {billingInfo.address || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between px-2.5 py-1 text-[11px]">
                    <span className="text-gray-400 flex-shrink-0">{t("billingCity")}</span>
                    <span className="text-gray-700 font-medium truncate max-w-[140px] ml-2">
                      {billingInfo.city ? `${billingInfo.city}, ${billingInfo.zip}` : "—"}
                    </span>
                  </div>
                </div>
              )}

              {showBillingExpanded && showBillingEdit && (
                <div className="space-y-2 mt-1.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t("billingEmail")}</label>
                    <input
                      type="email"
                      value={billingInfo.email}
                      onChange={(e) => handleBillingInputChange("email", e.target.value)}
                      className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder={t("billingEmailPlaceholder")}
                    />
                    {billingInfo.email && !validateEmail(billingInfo.email) && (
                      <p className="text-xs text-red-600 mt-1">{t("billingInvalidEmail")}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t("billingIban")}</label>
                    <input
                      type="text"
                      value={billingInfo.iban}
                      onChange={(e) => handleBillingInputChange("iban", e.target.value)}
                      className={`rounded-lg border h-8 block w-full px-3 text-xs focus:outline-none bg-white shadow-sm ${
                        billingInfo.iban && !ibanValid ? "border-red-200" : "border-gray-200 focus:border-primary-600"
                      }`}
                      placeholder={t("billingIbanPlaceholder")}
                    />
                    {billingInfo.iban && ibanValid === false && (
                      <p className="text-xs text-red-600 mt-1">{t("billingInvalidIban")}</p>
                    )}
                    {billingInfo.iban && ibanValid === true && (
                      <p className="text-xs text-green-600 mt-1">{t("billingValidIban")}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t("billingAddress")}
                    </label>
                    <input
                      type="text"
                      value={billingInfo.address}
                      onChange={(e) => handleBillingInputChange("address", e.target.value)}
                      className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder={t("billingAddressPlaceholder")}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      value={billingInfo.city}
                      onChange={(e) => handleBillingInputChange("city", e.target.value)}
                      className="col-span-2 rounded-lg border border-gray-200 h-8 px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder={t("billingCity")}
                    />
                    <input
                      type="text"
                      value={billingInfo.zip}
                      onChange={(e) => handleBillingInputChange("zip", e.target.value)}
                      className="rounded-lg border border-gray-200 h-8 px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                      placeholder={t("billingZip")}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t("billingCountry")}
                    </label>
                    <input
                      type="text"
                      value={billingInfo.country}
                      onChange={(e) => handleBillingInputChange("country", e.target.value)}
                      className="rounded-lg border border-gray-200 h-8 block w-full px-3 text-xs focus:border-primary-600 focus:outline-none bg-white shadow-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 px-4 py-3 bg-white border-t border-gray-200 mt-auto">
              {startDatePassed && (
                <div className="mb-2.5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.732 0 2.815-1.874 1.95-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                  <p className="text-xs text-amber-800 leading-relaxed">
                    {t("startDatePassedNotice", {
                      date: projectedStartDate,
                      effectiveDate: getFirstOfMonthOnOrAfter(todayStr),
                    })}
                  </p>
                </div>
              )}
              <div className="flex items-start gap-2 mb-2.5">
                <input
                  type="checkbox"
                  id="bess-resign-terms"
                  checked={hasAcceptedTerms}
                  onChange={(e) => handleAcceptTermsChange(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-primary-700 flex-shrink-0 cursor-pointer"
                />
                <label htmlFor="bess-resign-terms" className="text-xs text-gray-700 leading-snug cursor-pointer">
                  <Trans
                    i18nKey="termsAccept"
                    ns="bessresign"
                    values={{ companyName: pendingRow.company_name || "" }}
                    components={{
                      strong: <strong className="font-semibold text-gray-900" />,
                      termsLink: <span />,
                    }}
                  />
                </label>
              </div>

              <button
                onClick={handleSign}
                disabled={!canSign}
                className={`w-full h-9 rounded-lg flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors ${
                  canSign
                    ? "bg-primary-700 hover:bg-primary-800 text-white"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t("processing")}
                  </>
                ) : canSign ? (
                  t("signButton")
                ) : (
                  t("lockHintFinishReading")
                )}
              </button>

              {!canSign && !isProcessing && lockHint && (
                <p className="text-[11px] text-gray-400 text-center mt-1.5">{lockHint}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
